// Extracts question/answer pairs from a markdown FAQ body for FAQPage
// JSON-LD (docs/SEO-AUDIT.md §6.1). Reading them back out of the rendered
// content — rather than keeping a second hand-maintained copy — is what
// keeps the schema from drifting away from what the page actually says,
// the same reason Tools.jsx builds its schema from getFaqs(t).
//
// Questions are the h3s; the answer is everything up to the next heading.

// Schema.org wants answer text, not markup: unwrap links, strip emphasis
// and list/table syntax, and collapse the result onto one line.
const toPlainText = (markdown) => markdown
  .replace(/```[\s\S]*?```/g, ' ')
  .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
  .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
  .replace(/^\s*\|.*$/gm, ' ')
  .replace(/^\s*>\s?/gm, '')
  .replace(/^\s*[-*+]\s+/gm, '')
  .replace(/^\s*\d+\.\s+/gm, '')
  .replace(/[*_`~]/g, '')
  .replace(/\s+/g, ' ')
  .trim();

export const extractFaqs = (markdown) => {
  const faqs = [];
  let current = null;
  let inFence = false;
  for (const line of markdown.split('\n')) {
    if (/^\s*(```|~~~)/.test(line))
      inFence = !inFence;
    if (inFence) {
      if (current)
        current.lines.push(line);
      continue;
    }
    const h3 = line.match(/^###\s+(.+?)\s*#*\s*$/);
    if (h3) {
      current = { question: toPlainText(h3[1]), lines: [] };
      faqs.push(current);
      continue;
    }
    // Any other heading closes the open answer without starting a new one,
    // so section headings don't get swept into the previous answer.
    if (/^#{1,6}\s/.test(line)) {
      current = null;
      continue;
    }
    if (current)
      current.lines.push(line);
  }
  return faqs
    .map(({ question, lines }) => ({ question, answer: toPlainText(lines.join('\n')) }))
    .filter(faq => faq.question && faq.answer);
};

// FAQPage schema for a markdown body, or null when there's nothing to
// describe — an empty mainEntity array is invalid schema, so callers skip
// the <script> entirely rather than emitting one.
export const faqPageJsonLd = (markdown) => {
  const faqs = extractFaqs(markdown || '');
  if (!faqs.length)
    return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };
};
